import React from "react";
import { Flex, Heading, Text } from "theme-ui";
import { graphql, useStaticQuery } from "gatsby";
import { GatsbyImage, getImage, withArtDirection } from "gatsby-plugin-image";
import { colors, typography } from "../../styles/theme";

const pointerArr = [
  {
    title: "Meticulous integration",
    point1: "Experts drive end-to-end account set-up",
    point2: "2 months of onboarding support",
  },
  {
    title: "Proactive customer support",
    point1: "First response time under 2 hours",
    point2: "Resolution time under 6 hours for 85% queries",
  },
  {
    title: "Coaching for HR & payroll personnel",
    point1: "Live product training sessions via webinars",
    point2: "Multilingual training",
  },
];

const SupportSection = ({ code }) => {
  const data = useStaticQuery(graphql`
    {
      supportMeDesktop: file(
        relativePath: { eq: "home/support-me-desktop.png" }
      ) {
        childImageSharp {
          gatsbyImageData(height: 304, width: 1186, layout: CONSTRAINED)
        }
      }
      supportMeTab: file(relativePath: { eq: "home/support-me-tab.png" }) {
        childImageSharp {
          gatsbyImageData(height: 300, width: 704, layout: CONSTRAINED)
        }
      }
      supportMeMobile: file(
        relativePath: { eq: "home/support-me-mobile.png" }
      ) {
        childImageSharp {
          gatsbyImageData(height: 300, width: 364, layout: CONSTRAINED)
        }
      }
      supportIdDesktop: file(
        relativePath: { eq: "home/support-id-desktop.png" }
      ) {
        childImageSharp {
          gatsbyImageData(height: 304, width: 1186, layout: CONSTRAINED)
        }
      }
      supportIdTab: file(relativePath: { eq: "home/support-id-desktop.png" }) {
        childImageSharp {
          gatsbyImageData(height: 300, width: 704, layout: CONSTRAINED)
        }
      }
      supportIdMobile: file(
        relativePath: { eq: "home/support-id-mobile.png" }
      ) {
        childImageSharp {
          gatsbyImageData(height: 300, width: 364, layout: CONSTRAINED)
        }
      }
      supportSgDesktop: file(
        relativePath: { eq: "home/support-sg-desktop.png" }
      ) {
        childImageSharp {
          gatsbyImageData(height: 304, width: 1186, layout: CONSTRAINED)
        }
      }
      supportSgTab: file(relativePath: { eq: "home/support-sg-desktop.png" }) {
        childImageSharp {
          gatsbyImageData(height: 300, width: 704, layout: CONSTRAINED)
        }
      }
      supportSgMobile: file(
        relativePath: { eq: "home/support-sg-mobile.png" }
      ) {
        childImageSharp {
          gatsbyImageData(height: 300, width: 364, layout: CONSTRAINED)
        }
      }
      supportMyDesktop: file(
        relativePath: { eq: "home/support-my-desktop.png" }
      ) {
        childImageSharp {
          gatsbyImageData(height: 304, width: 1186, layout: CONSTRAINED)
        }
      }
      supportMyTab: file(relativePath: { eq: "home/support-my-tab.png" }) {
        childImageSharp {
          gatsbyImageData(height: 300, width: 704, layout: CONSTRAINED)
        }
      }
      supportMyMobile: file(
        relativePath: { eq: "home/support-my-mobile.png" }
      ) {
        childImageSharp {
          gatsbyImageData(height: 300, width: 364, layout: CONSTRAINED)
        }
      }
    }
  `);

  const imageMap = {
    ME: {
      desktop: data.supportMeDesktop,
      tablet: data.supportMeTab,
      mobile: data.supportMeMobile,
    },
    ID: {
      desktop: data.supportIdDesktop,
      tablet: data.supportIdTab,
      mobile: data.supportIdMobile,
    },
    SG: {
      desktop: data.supportSgDesktop,
      tablet: data.supportSgTab,
      mobile: data.supportSgMobile,
    },
    MY: {
      desktop: data.supportMyDesktop,
      tablet: data.supportMyTab,
      mobile: data.supportMyMobile,
    },
  };

  const selectedImages = imageMap[code];

  const images = withArtDirection(getImage(selectedImages.desktop), [
    {
      media: "(max-width: 900px)",
      image: getImage(selectedImages.tablet),
    },
    {
      media: "(max-width: 600px)",
      image: getImage(selectedImages.mobile),
    },
  ]);

  return (
    <Flex sx={styles.container}>
      <Heading as="h2" sx={{ ...typography.h2, textAlign: "center" }}>
        Minimise downtime and{" "}
        <span style={{ color: colors.primary700 }}>maximise productivity</span>
      </Heading>
      <GatsbyImage
        image={images}
        alt="support image"
        style={{ borderRadius: "1.25rem", height: "18.75rem" }}
      />
      <Flex sx={styles.listContainer}>
        {pointerArr.map((item, index) => (
          <Flex key={`point-${index}`} sx={{ flexDirection: "column" }}>
            <Text sx={typography.h5}>{item?.title}</Text>
            <ul style={{ ...typography.textMd400, marginLeft: "-1.5rem" }}>
              <li>{item?.point1}</li>
              <li>{item?.point2}</li>
            </ul>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};

const styles = {
  container: {
    flexDirection: "column",
    py: ["4rem", "4rem", "7rem", "7rem"],
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.secondaryBlue100,
    gap: "3rem",
    "@media screen and (max-width: 1240px)": {
      padding: "7rem 1rem 7rem 1rem",
    },
    "@media screen and (max-width: 600px)": {
      padding: "4rem 1rem 4rem 1rem",
    },
  },
  listContainer: {
    gap: "1.875rem",
    width: "100%",
    justifyContent: "center",
    flexDirection: ["column", "column", "row", "row"],
  },
};

export default React.memo(SupportSection);
